import { c as _c } from "react/compiler-runtime";
import ThemedAvatar from 'shared/components/Themed/Avatar';
import Badge, { type BadgeColor } from '@precis-digital/kurama/Badge';
import Button from '@precis-digital/kurama/Button';
import Chip from '@precis-digital/kurama/Chip';
import DocumentationIcon from '@precis-digital/kurama/icons/DocumentationIcon';
import Grid from '@precis-digital/kurama/Grid';
import LinkIcon from '@precis-digital/kurama/icons/LinkIcon';
import MultiSelectDropdown, { type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown';
import PlusIcon from '@precis-digital/kurama/icons/PlusIcon';
import Tooltip, { type TooltipProps, TooltipRichCaption, TooltipRichHeader } from '@precis-digital/kurama/Tooltip';
import { useCallback, useEffect, useMemo, type ReactElement, type ReactNode } from 'react';
import { type Platforms } from 'shared/api/accounts';
import { type CorpusResponse } from 'shared/api/copilot';
import { capitalize } from 'shared/utils/stringFormatter';
import { useCopilot } from 'shared/context/CopilotContext';
import { useCurrentClient } from 'shared/context/ClientContext';
import { useChangePath } from 'shared/components/Router';
import States from 'shared/components/States';
import StatusTooltipBody from 'shared/components/StatusTooltipBody';
import { APP_ROUTES } from 'shared/routes';
import { useTranslation, type TFunction } from 'shared/translations';
import { getPlatformDetailsByPlatform } from 'src/dataSource/utils';
import { useKnowledgeSources, useCompanyWideKnowledgeSources } from 'shared/copilot/api/knowledgeSources';
import { useShouldShowInternalFeatures } from 'shared/hooks/useShouldShowInternalFeatures';
import { useAlvieDatasourceOptions } from '../hooks/useAlvieDatasourceOptions';
import { useCopilotCirrusSourceOptions } from '../hooks/useCopilotCirrusSourceOptions';
import { buildCopilotSourceMenuOptions, buildKnowledgeOnlySourceMenuOptions, COPILOT_DATA_SOURCES_MENU_VALUE, COPILOT_KNOWLEDGE_SOURCES_MENU_VALUE, getCirrusCollectionIcon, mapAlvieDatasourceToSubOption, mapKnowledgeSourceToSubOption, type FormatLastUpdateFn, type PipelineLabelFn, type StatusLabelFn } from '../utils/buildCopilotSourceMenuOptions';
import { type AlvieDatasourceOption } from '../utils/alvieDatasourceOptions';
import { type CirrusSourceOption, isCirrusOptionId } from '../cirrusSource';
import { formatDateTimeToAtFormat } from 'shared/dateFns';
import { getErrorCodeDetails, type ConfigStatus } from 'shared/configStatuses';
import { findCirrusSourceOption, resolveLiveCirrusSourceOption } from '../utils/cirrusSourceOptions';
import { getCirrusChipTooltipModel } from '../utils/getCirrusChipTooltipModel';
const getDatasourceSourceTypeLabel = (platform: AlvieDatasourceOption['platform']): string => {
  return getPlatformDetailsByPlatform(platform)?.label ?? platform;
};
const getDatasourcePlatformAvatar = (platform: Platforms): ReactElement | undefined => {
  const platformDetails = getPlatformDetailsByPlatform(platform);
  return platformDetails?.iconUrl != null ? <ThemedAvatar kind="image" imageUrl={platformDetails.iconUrl} size="small" /> : undefined;
};
const groupDatasourcesByPlatform = (datasources: AlvieDatasourceOption[]): Map<Platforms, AlvieDatasourceOption[]> => {
  const groups = new Map<Platforms, AlvieDatasourceOption[]>();
  for (const datasource of datasources) {
    const existing = groups.get(datasource.platform) ?? [];
    groups.set(datasource.platform, [...existing, datasource]);
  }
  return groups;
};
const KNOWLEDGE_SOURCE_CHIP_AVATAR = <DocumentationIcon />;
interface SelectedChipTooltipContentParams {
  avatar?: ReactElement;
  headerTitle: string;
  headerSubtitle?: string;
  detailCaptions?: string[];
  itemLabels?: string[];
  statusText: string;
  statusColor: BadgeColor;
  trailingCaption?: string;
  footer?: ReactNode;
}
const buildSelectedChipTooltipContent = ({
  avatar,
  headerTitle,
  headerSubtitle,
  detailCaptions,
  itemLabels,
  statusText,
  statusColor,
  trailingCaption,
  footer
}: SelectedChipTooltipContentParams): ReactElement => <Grid container flexDirection="column" gap="8px">
    <TooltipRichCaption text={statusText} statusIndicator={<Badge color={statusColor} variant="lighter" />} />
    <TooltipRichHeader avatar={avatar} title={headerTitle} subtitle={headerSubtitle} />
    {detailCaptions?.map(caption => <TooltipRichCaption key={caption} text={caption} />)}
    {itemLabels != null && itemLabels.length > 0 ? <Grid container flexDirection="column" gap="4px">
        {itemLabels.map((label, index) => <TooltipRichCaption key={`${label}-${index}`} text={`· ${label}`} />)}
      </Grid> : null}
    {trailingCaption != null ? <TooltipRichCaption text={trailingCaption} /> : null}
    {footer}
  </Grid>;
const getSelectedChipTooltipProps = (content: ReactElement): TooltipProps => ({
  kind: 'multiline',
  shouldDelay: true,
  width: 'fit-content',
  content
});
const getGroupedDatasourceChipTooltipProps = (platform: Platforms, datasources: AlvieDatasourceOption[], t: TFunction<'common', 'copilot'>): TooltipProps => {
  const platformDetails = getPlatformDetailsByPlatform(platform);
  const platformLabel = getDatasourceSourceTypeLabel(platform);
  const entityLabel = capitalize(platformDetails?.externalEntitiesLabel ?? 'accounts');
  const avatar = getDatasourcePlatformAvatar(platform);
  const hasDisconnectedSource = datasources.some(datasource => !datasource.connected);
  return getSelectedChipTooltipProps(buildSelectedChipTooltipContent({
    avatar,
    headerTitle: platformLabel,
    headerSubtitle: t('dataAgent.sourceTooltip.groupEntityCount', {
      count: datasources.length,
      entityLabel
    }),
    itemLabels: datasources.map(datasource => datasource.label),
    statusText: hasDisconnectedSource ? t('dataAgent.sourceTooltip.disconnected') : t('dataAgent.sourceTooltip.currentlySelected'),
    statusColor: hasDisconnectedSource ? 'semantic-error' : 'semantic-success',
    trailingCaption: hasDisconnectedSource ? t('dataAgent.sourceTooltip.reconnectBody') : undefined
  }));
};
const getCirrusChipTooltipProps = ({
  option,
  t,
  tCommon,
  formatLastUpdate,
  getPipelineLabel,
  getStatusLabel
}: {
  option: CirrusSourceOption;
  t: TFunction<'common', 'copilot'>;
  tCommon: TFunction;
  formatLastUpdate: FormatLastUpdateFn;
  getPipelineLabel: PipelineLabelFn;
  getStatusLabel: StatusLabelFn;
}): TooltipProps => {
  const model = getCirrusChipTooltipModel({
    option,
    t,
    tCommon,
    formatLastUpdate,
    getStatusLabel
  });
  const {
    status,
    statusDetail
  } = model;
  return getSelectedChipTooltipProps(buildSelectedChipTooltipContent({
    avatar: getCirrusCollectionIcon(model.collection),
    headerTitle: model.headerTitle,
    headerSubtitle: model.headerSubtitle,
    detailCaptions: model.detailCaptions.length > 0 ? model.detailCaptions : undefined,
    statusText: model.statusText,
    statusColor: model.statusColor,
    footer: model.showStatusBody && status != null && statusDetail != null ? <StatusTooltipBody status={status} hasLinkToDagView={false} errorCode={statusDetail.errorCode} errorDetails={getErrorCodeDetails(statusDetail.errorCode, statusDetail.errorMessage, statusDetail.targetProjectId, getPipelineLabel(model.collection))} /> : undefined
  }));
};
const getKnowledgeSourceChipTooltipProps = (corpora: CorpusResponse[], t: TFunction<'common', 'copilot'>): TooltipProps => {
  if (corpora.length === 1) {
    const corpus = corpora[0];
    return getSelectedChipTooltipProps(buildSelectedChipTooltipContent({
      avatar: KNOWLEDGE_SOURCE_CHIP_AVATAR,
      headerTitle: corpus.name,
      headerSubtitle: t('dataAgent.sourceTooltip.knowledgeSourceType'),
      detailCaptions: corpus.documentCount > 0 ? [t('dataAgent.knowledgeSourceDocCount', {
        count: corpus.documentCount
      })] : undefined,
      statusText: t('dataAgent.sourceTooltip.currentlySelected'),
      statusColor: 'semantic-success'
    }));
  }
  return getSelectedChipTooltipProps(buildSelectedChipTooltipContent({
    avatar: KNOWLEDGE_SOURCE_CHIP_AVATAR,
    headerTitle: t('sourceMenu.knowledgeSources'),
    headerSubtitle: t('dataAgent.knowledgeSourceChipCount', {
      count: corpora.length
    }),
    itemLabels: corpora.map(corpus => corpus.name),
    statusText: t('dataAgent.sourceTooltip.currentlySelected'),
    statusColor: 'semantic-success'
  }));
};
const getDatasourceTooltipProps = (option: AlvieDatasourceOption, t: TFunction<'common', 'copilot'>, {
  isSelected = false,
  isOtherSourceLocked = false
}: {
  isSelected?: boolean;
  isOtherSourceLocked?: boolean;
} = {}): TooltipProps => {
  if (isOtherSourceLocked) {
    return {
      kind: 'singleline',
      title: t('dataAgent.sourceTooltip.otherSourceLocked')
    };
  }
  if (!option.supported) {
    return {
      kind: 'singleline',
      title: t('dataAgent.sourceTooltip.unsupportedPlatform')
    };
  }

  // Connected, but Alvie has not resolved the platform metadata the agent needs
  // to query it. Reconnecting is what fills it in, so say that here rather than
  // letting the chat fail on the user's first question. A disconnected account
  // keeps its own richer tooltip below — reconnecting fixes both.
  if (option.connected && option.needsSetup) {
    return {
      kind: 'singleline',
      title: t('dataAgent.sourceTooltip.needsSetup')
    };
  }
  const platformDetails = getPlatformDetailsByPlatform(option.platform);
  const typeLabel = getDatasourceSourceTypeLabel(option.platform);
  const avatar = platformDetails?.iconUrl != null ? <ThemedAvatar kind="image" imageUrl={platformDetails.iconUrl} size="small" /> : undefined;
  if (isSelected) {
    return getSelectedChipTooltipProps(buildSelectedChipTooltipContent({
      avatar,
      headerTitle: option.label,
      headerSubtitle: typeLabel,
      detailCaptions: [t('dataAgent.sourceTooltip.accountId', {
        accountId: option.subLabel
      })],
      statusText: option.connected ? t('dataAgent.sourceTooltip.currentlySelected') : t('dataAgent.sourceTooltip.disconnected'),
      statusColor: option.connected ? 'semantic-success' : 'semantic-error',
      trailingCaption: option.connected ? undefined : t('dataAgent.sourceTooltip.reconnectBody')
    }));
  }
  const statusCaptionText = option.connected ? t('dataAgent.sourceTooltip.currentlySelected') : t('dataAgent.sourceTooltip.reconnectTitle');
  return {
    kind: 'multiline',
    shouldDelay: true,
    width: 'fit-content',
    content: <Grid container flexDirection="column" gap="8px">
        {!option.connected ? <TooltipRichCaption text={statusCaptionText} statusIndicator={<Badge color={option.connected ? 'semantic-success' : 'semantic-error'} variant="lighter" />} /> : null}
        <TooltipRichHeader avatar={avatar} title={option.label} subtitle={typeLabel} />
        <TooltipRichCaption text={t('dataAgent.sourceTooltip.accountId', {
        accountId: option.subLabel
      })} />
        {!option.connected ? <TooltipRichCaption text={t('dataAgent.sourceTooltip.reconnectBody')} /> : null}
      </Grid>
  };
};
interface SourceChipModel {
  key: string;
  label: string;
  avatar?: ReactElement;
  tooltipProps: TooltipProps;
  onDelete: () => void;
  isError?: boolean;
}
interface CopilotDatasourceControlsProps {
  onMenuClosed?: () => void;
  /** Drawer sidenav: knowledge sources only. Fullscreen Alvie AI Pro: data + knowledge. */
  variant?: 'all' | 'knowledge-only';
}

/**
 * Shared source picker and selected chips for drawer (knowledge-only) and Alvie AI Pro (all sources).
 */
export const CopilotDatasourceControls = t0 => {
  const $ = _c(172);
  const {
    onMenuClosed,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "all" : t1;
  const isKnowledgeOnly = variant === "knowledge-only";
  const {
    selectedDatasourceIds,
    selectedCirrusSource,
    setSelectedDatasources,
    setSelectedCirrusSource,
    clearSelectedDatasource,
    reconcileDatasourceWithAvailableOptions,
    selectedKnowledgeSourceIds,
    setSelectedKnowledgeSources,
    clearSelectedKnowledgeSource,
    reconcileKnowledgeSourcesWithAvailableCorpora
  } = useCopilot();
  const {
    currentClient
  } = useCurrentClient();
  const {
    changePath
  } = useChangePath();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "copilot"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("common", t2);
  const {
    t: tCommon
  } = useTranslation("common");
  const {
    options,
    isLoading: isLoadingSources,
    hasSources
  } = useAlvieDatasourceOptions(currentClient.id, !isKnowledgeOnly);
  const cirrusGroups = useCopilotCirrusSourceOptions(currentClient.id, t("sourceMenu.recommendations"));
  const clientId = currentClient.id;
  const showInternalFeatures = useShouldShowInternalFeatures();
  const {
    data: knowledgeSourcesData,
    isLoading: isLoadingKnowledgeSources
  } = useKnowledgeSources(clientId, true);
  const {
    data: companyWideKnowledgeSourcesData,
    isLoading: isLoadingCompanyWideKnowledgeSources
  } = useCompanyWideKnowledgeSources(clientId, showInternalFeatures);
  let t3;
  if ($[1] !== knowledgeSourcesData) {
    t3 = knowledgeSourcesData ?? [];
    $[1] = knowledgeSourcesData;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t3) {
    t4 = t3.filter(_temp);
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const workspaceCorpora = t4;
  let t5;
  if ($[5] !== companyWideKnowledgeSourcesData) {
    t5 = companyWideKnowledgeSourcesData ?? [];
    $[5] = companyWideKnowledgeSourcesData;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const companyWideCorpora = t5;
  let t6;
  if ($[7] !== companyWideCorpora || $[8] !== workspaceCorpora) {
    t6 = [...workspaceCorpora, ...companyWideCorpora];
    $[7] = companyWideCorpora;
    $[8] = workspaceCorpora;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const availableCorpora = t6;
  const hasKnowledgeSources = availableCorpora.length > 0;
  const selectedDatasourceId = selectedDatasourceIds[0];
  let t7;
  if ($[10] !== options) {
    t7 = new Set(options.map(_temp2));
    $[10] = options;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const datasourceIdSet = t7;
  let t8;
  if ($[12] !== availableCorpora) {
    t8 = new Set(availableCorpora.map(_temp3));
    $[12] = availableCorpora;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const knowledgeSourceIdSet = t8;
  let t9;
  if ($[14] !== tCommon) {
    t9 = lastUpdated => lastUpdated == null ? tCommon("pending") : formatDateTimeToAtFormat(lastUpdated);
    $[14] = tCommon;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const formatLastUpdate = t9;
  let t10;
  if ($[16] !== tCommon) {
    t10 = collection => collection === "standard_reports" ? tCommon("dagView.reportingSolution") : collection === "attribution" ? tCommon("dagView.attributionModel") : collection === "bo" ? tCommon("dagView.budgetOptimiser") : tCommon("dagView.connector");
    $[16] = tCommon;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const getPipelineLabel = t10;
  let t11;
  if ($[18] !== tCommon) {
    t11 = status => tCommon(`status.${status}`);
    $[18] = tCommon;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const getStatusLabel = t11;
  let t12;
  bb0: {
    if (isKnowledgeOnly) {
      let t13;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = [];
        $[20] = t13;
      } else {
        t13 = $[20];
      }
      t12 = t13;
      break bb0;
    }
    let t13;
    if ($[21] !== options || $[22] !== selectedDatasourceId || $[23] !== t) {
      let t14;
      if ($[25] !== selectedDatasourceId || $[26] !== t) {
        t14 = option_0 => {
          const platformDetails = getPlatformDetailsByPlatform(option_0.platform);
          const isOtherSourceLocked = selectedDatasourceId != null && option_0.id !== selectedDatasourceId;
          return mapAlvieDatasourceToSubOption({
            option: option_0,
            platformLabel: getDatasourceSourceTypeLabel(option_0.platform),
            tooltipProps: getDatasourceTooltipProps(option_0, t, {
              isOtherSourceLocked
            }),
            disabled: !option_0.supported || !option_0.connected || option_0.needsSetup || isOtherSourceLocked,
            avatar: platformDetails?.iconUrl != null ? <ThemedAvatar kind="image" imageUrl={platformDetails.iconUrl} size="small" /> : undefined
          });
        };
        $[25] = selectedDatasourceId;
        $[26] = t;
        $[27] = t14;
      } else {
        t14 = $[27];
      }
      t13 = options.map(t14);
      $[21] = options;
      $[22] = selectedDatasourceId;
      $[23] = t;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    t12 = t13;
  }
  const datasourceSubOptions = t12;
  const workspaceLabel = t("dataAgent.knowledgeSourceGroupWorkspace");
  const companyWideLabel = t("dataAgent.knowledgeSourceGroupCompanyWide");
  const shouldGroup = showInternalFeatures && companyWideCorpora.length > 0;
  const byName = _temp4;
  let t13;
  if ($[28] !== companyWideCorpora || $[29] !== companyWideLabel || $[30] !== shouldGroup || $[31] !== t || $[32] !== workspaceCorpora || $[33] !== workspaceLabel) {
    const workspaceOptions = workspaceCorpora.toSorted(byName).map(corpus_1 => mapKnowledgeSourceToSubOption({
      id: corpus_1.id,
      name: corpus_1.name,
      subCategory: shouldGroup ? workspaceLabel : undefined,
      annotation: corpus_1.documentCount > 0 ? t("dataAgent.knowledgeSourceDocCount", {
        count: corpus_1.documentCount
      }) : undefined
    }));
    const companyWideOptions = companyWideCorpora.toSorted(byName).map(corpus_2 => mapKnowledgeSourceToSubOption({
      id: corpus_2.id,
      name: corpus_2.name,
      subCategory: companyWideLabel,
      annotation: corpus_2.documentCount > 0 ? t("dataAgent.knowledgeSourceDocCount", {
        count: corpus_2.documentCount
      }) : undefined
    }));
    t13 = [...workspaceOptions, ...companyWideOptions];
    $[28] = companyWideCorpora;
    $[29] = companyWideLabel;
    $[30] = shouldGroup;
    $[31] = t;
    $[32] = workspaceCorpora;
    $[33] = workspaceLabel;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const knowledgeSourceSubOptions = t13;
  const t14 = t("dataAgent.dataSourcesEmpty.imageAlt");
  const t15 = t("dataAgent.dataSourcesEmpty.title");
  const t16 = t("dataAgent.dataSourcesEmpty.description");
  const t17 = t("dataAgent.dataSourcesEmpty.cta");
  let t18;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <LinkIcon />;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== changePath || $[37] !== onMenuClosed) {
    t19 = () => {
      onMenuClosed?.();
      changePath(APP_ROUTES.dataSources.basePage);
    };
    $[36] = changePath;
    $[37] = onMenuClosed;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t14 || $[40] !== t15 || $[41] !== t16 || $[42] !== t17 || $[43] !== t19) {
    t20 = <States size="small" variant="actionInsideAlvie" imageAlt={t14} title={t15} description={t16} primaryButtonLabel={t17} primaryButtonRightIcon={t18} primaryButtonCallback={t19} />;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const dataSourcesEmptyContent = t20;
  const t21 = t("dataAgent.knowledgeSourcesEmpty.imageAlt");
  const t22 = t("dataAgent.knowledgeSourcesEmpty.title");
  const t23 = t("dataAgent.knowledgeSourcesEmpty.description");
  const t24 = t("dataAgent.knowledgeSourcesEmpty.cta");
  let t25;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <LinkIcon />;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== changePath || $[47] !== onMenuClosed) {
    t26 = () => {
      onMenuClosed?.();
      changePath(APP_ROUTES.copilot.settingsKnowledgeSourcesPage);
    };
    $[46] = changePath;
    $[47] = onMenuClosed;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== t21 || $[50] !== t22 || $[51] !== t23 || $[52] !== t24 || $[53] !== t26) {
    t27 = <States size="small" variant="actionInsideAlvie" imageAlt={t21} title={t22} description={t23} primaryButtonLabel={t24} primaryButtonRightIcon={t25} primaryButtonCallback={t26} />;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
    $[53] = t26;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  const knowledgeSourcesEmptyContent = t27;
  const t28 = t("dataAgent.knowledgeSourcesWorkspaceEmpty.imageAlt");
  const t29 = t("dataAgent.knowledgeSourcesWorkspaceEmpty.title");
  const t30 = companyWideCorpora.length > 0 ? t("dataAgent.knowledgeSourcesWorkspaceEmpty.descriptionWithCompanyWide") : t("dataAgent.knowledgeSourcesWorkspaceEmpty.description");
  const t31 = t("dataAgent.knowledgeSourcesWorkspaceEmpty.cta");
  let t32;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <LinkIcon />;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] !== changePath || $[57] !== onMenuClosed) {
    t33 = () => {
      onMenuClosed?.();
      changePath(APP_ROUTES.copilot.settingsKnowledgeSourcesPage);
    };
    $[56] = changePath;
    $[57] = onMenuClosed;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  let t34;
  if ($[59] !== t28 || $[60] !== t29 || $[61] !== t30 || $[62] !== t31 || $[63] !== t33) {
    t34 = <States size="small" variant="actionInsideAlvie" imageAlt={t28} title={t29} description={t30} primaryButtonLabel={t31} primaryButtonRightIcon={t32} primaryButtonCallback={t33} />;
    $[59] = t28;
    $[60] = t29;
    $[61] = t30;
    $[62] = t31;
    $[63] = t33;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  const knowledgeSourcesWorkspaceEmptyContent = t34;
  let t35;
  if ($[65] !== cirrusGroups || $[66] !== datasourceSubOptions || $[67] !== formatLastUpdate || $[68] !== getPipelineLabel || $[69] !== getStatusLabel || $[70] !== isKnowledgeOnly || $[71] !== knowledgeSourceSubOptions || $[72] !== selectedDatasourceId || $[73] !== t) {
    t35 = isKnowledgeOnly ? buildKnowledgeOnlySourceMenuOptions({
      knowledgeSourceSubOptions,
      t
    }) : buildCopilotSourceMenuOptions({
      datasourceSubOptions,
      knowledgeSourceSubOptions,
      cirrusGroups,
      selectedOptionId: selectedDatasourceId,
      formatLastUpdate,
      getPipelineLabel,
      getStatusLabel,
      t
    });
    $[65] = cirrusGroups;
    $[66] = datasourceSubOptions;
    $[67] = formatLastUpdate;
    $[68] = getPipelineLabel;
    $[69] = getStatusLabel;
    $[70] = isKnowledgeOnly;
    $[71] = knowledgeSourceSubOptions;
    $[72] = selectedDatasourceId;
    $[73] = t;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  const sourceMenuOptions = t35;
  const workspaceKnowledgeSourceGroupLabel = t("dataAgent.knowledgeSourceGroupWorkspace");
  let t36;
  if ($[75] !== dataSourcesEmptyContent || $[76] !== hasKnowledgeSources || $[77] !== hasSources || $[78] !== knowledgeSourcesEmptyContent) {
    const map = {};
    if (!hasSources) {
      map[COPILOT_DATA_SOURCES_MENU_VALUE] = dataSourcesEmptyContent;
    }
    if (!hasKnowledgeSources) {
      map[COPILOT_KNOWLEDGE_SOURCES_MENU_VALUE] = knowledgeSourcesEmptyContent;
    }
    t36 = Object.keys(map).length > 0 ? map : undefined;
    $[75] = dataSourcesEmptyContent;
    $[76] = hasKnowledgeSources;
    $[77] = hasSources;
    $[78] = knowledgeSourcesEmptyContent;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  const subOptionsEmptyContent = t36;
  let t37;
  bb1: {
    const shouldShowWorkspaceEmpty = showInternalFeatures && companyWideCorpora.length > 0 && workspaceCorpora.length === 0;
    if (!shouldShowWorkspaceEmpty) {
      t37 = undefined;
      break bb1;
    }
    let t38;
    if ($[80] !== knowledgeSourcesWorkspaceEmptyContent || $[81] !== workspaceKnowledgeSourceGroupLabel) {
      t38 = {
        [COPILOT_KNOWLEDGE_SOURCES_MENU_VALUE]: {
          [workspaceKnowledgeSourceGroupLabel]: knowledgeSourcesWorkspaceEmptyContent
        }
      };
      $[80] = knowledgeSourcesWorkspaceEmptyContent;
      $[81] = workspaceKnowledgeSourceGroupLabel;
      $[82] = t38;
    } else {
      t38 = $[82];
    }
    t37 = t38;
  }
  const subCategoryEmptyContent = t37;
  let t38;
  if ($[83] !== isKnowledgeOnly || $[84] !== selectedDatasourceIds || $[85] !== selectedKnowledgeSourceIds) {
    t38 = isKnowledgeOnly ? selectedKnowledgeSourceIds : [...selectedDatasourceIds, ...selectedKnowledgeSourceIds];
    $[83] = isKnowledgeOnly;
    $[84] = selectedDatasourceIds;
    $[85] = selectedKnowledgeSourceIds;
    $[86] = t38;
  } else {
    t38 = $[86];
  }
  const menuValue = t38;
  let t39;
  if ($[87] !== clearSelectedDatasource || $[88] !== datasourceSubOptions || $[89] !== options || $[90] !== selectedCirrusSource || $[91] !== selectedDatasourceIds || $[92] !== setSelectedDatasources || $[93] !== t) {
    bb2: {
      if (selectedCirrusSource != null) {
        let t40;
        if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
          t40 = [];
          $[95] = t40;
        } else {
          t40 = $[95];
        }
        t39 = t40;
        break bb2;
      }
      const selectedDatasources = selectedDatasourceIds.flatMap(datasourceId => {
        const datasource = options.find(option_1 => option_1.id === datasourceId);
        return datasource != null ? [datasource] : [];
      });
      if (selectedDatasources.length === 0) {
        let t40;
        if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
          t40 = [];
          $[96] = t40;
        } else {
          t40 = $[96];
        }
        t39 = t40;
        break bb2;
      }
      const datasourcesByPlatform = groupDatasourcesByPlatform(selectedDatasources);
      t39 = Array.from(datasourcesByPlatform.entries()).flatMap(t40 => {
        const [platform, platformDatasources] = t40;
        const avatar = getDatasourcePlatformAvatar(platform);
        if (platformDatasources.length === 1) {
          const datasource_0 = platformDatasources[0];
          const datasourceOption = datasourceSubOptions.find(option_2 => option_2.value === datasource_0.id);
          return [{
            key: datasource_0.id,
            label: datasourceOption?.label ?? datasource_0.label ?? t("dataAgent.selectedSourceFallback"),
            avatar,
            tooltipProps: getDatasourceTooltipProps(datasource_0, t, {
              isSelected: true
            }),
            onDelete: () => {
              clearSelectedDatasource(datasource_0.id);
            },
            isError: !datasource_0.connected
          }];
        }
        const platformDetails_0 = getPlatformDetailsByPlatform(platform);
        const platformLabel = getDatasourceSourceTypeLabel(platform);
        const entityLabel = capitalize(platformDetails_0?.externalEntitiesLabel ?? "accounts");
        const platformDatasourceIds = new Set(platformDatasources.map(_temp5));
        return [{
          key: `data-sources-${platform}`,
          label: t("dataAgent.dataSourceTypeChipCount", {
            count: platformDatasources.length,
            platformLabel,
            entityLabel
          }),
          avatar,
          tooltipProps: getGroupedDatasourceChipTooltipProps(platform, platformDatasources, t),
          onDelete: () => {
            const nextDatasourceIds = selectedDatasourceIds.filter(id => !platformDatasourceIds.has(id));
            setSelectedDatasources(nextDatasourceIds, options);
          },
          isError: platformDatasources.some(_temp6)
        }];
      });
    }
    $[87] = clearSelectedDatasource;
    $[88] = datasourceSubOptions;
    $[89] = options;
    $[90] = selectedCirrusSource;
    $[91] = selectedDatasourceIds;
    $[92] = setSelectedDatasources;
    $[93] = t;
    $[94] = t39;
  } else {
    t39 = $[94];
  }
  const selectedDatasourceChips = t39;
  let t40;
  bb3: {
    if (selectedCirrusSource == null) {
      t40 = null;
      break bb3;
    }
    let t41;
    let t42;
    let t43;
    let t44;
    if ($[97] !== cirrusGroups.allOptions || $[98] !== formatLastUpdate || $[99] !== getPipelineLabel || $[100] !== getStatusLabel || $[101] !== selectedCirrusSource || $[102] !== t || $[103] !== tCommon) {
      const liveOption = resolveLiveCirrusSourceOption(selectedCirrusSource, cirrusGroups.allOptions);
      t41 = liveOption.id;
      t42 = liveOption.label;
      t43 = getCirrusCollectionIcon(liveOption.collection);
      t44 = getCirrusChipTooltipProps({
        option: liveOption,
        t,
        tCommon,
        formatLastUpdate,
        getPipelineLabel,
        getStatusLabel
      });
      $[97] = cirrusGroups.allOptions;
      $[98] = formatLastUpdate;
      $[99] = getPipelineLabel;
      $[100] = getStatusLabel;
      $[101] = selectedCirrusSource;
      $[102] = t;
      $[103] = tCommon;
      $[104] = t41;
      $[105] = t42;
      $[106] = t43;
      $[107] = t44;
    } else {
      t41 = $[104];
      t42 = $[105];
      t43 = $[106];
      t44 = $[107];
    }
    let t45;
    if ($[108] !== clearSelectedDatasource || $[109] !== selectedCirrusSource) {
      t45 = () => {
        clearSelectedDatasource(selectedCirrusSource.id);
      };
      $[108] = clearSelectedDatasource;
      $[109] = selectedCirrusSource;
      $[110] = t45;
    } else {
      t45 = $[110];
    }
    let t46;
    if ($[111] !== t41 || $[112] !== t42 || $[113] !== t43 || $[114] !== t44 || $[115] !== t45) {
      t46 = {
        key: t41,
        label: t42,
        avatar: t43,
        tooltipProps: t44,
        onDelete: t45
      };
      $[111] = t41;
      $[112] = t42;
      $[113] = t43;
      $[114] = t44;
      $[115] = t45;
      $[116] = t46;
    } else {
      t46 = $[116];
    }
    t40 = t46;
  }
  const selectedCirrusChip = t40;
  let t41;
  if ($[117] !== availableCorpora || $[118] !== clearSelectedKnowledgeSource || $[119] !== selectedKnowledgeSourceIds || $[120] !== t) {
    bb4: {
      let t42;
      if ($[122] !== availableCorpora) {
        t42 = corpusId => {
          const corpus_3 = availableCorpora.find(item => item.id === corpusId);
          return corpus_3 != null ? [corpus_3] : [];
        };
        $[122] = availableCorpora;
        $[123] = t42;
      } else {
        t42 = $[123];
      }
      const selectedCorpora = selectedKnowledgeSourceIds.flatMap(t42);
      if (selectedCorpora.length === 0) {
        let t43;
        if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
          t43 = [];
          $[124] = t43;
        } else {
          t43 = $[124];
        }
        t41 = t43;
        break bb4;
      }
      if (selectedCorpora.length === 1) {
        const corpus_4 = selectedCorpora[0];
        t41 = [{
          key: corpus_4.id,
          label: corpus_4.name,
          avatar: KNOWLEDGE_SOURCE_CHIP_AVATAR,
          tooltipProps: getKnowledgeSourceChipTooltipProps(selectedCorpora, t),
          onDelete: () => {
            clearSelectedKnowledgeSource(corpus_4.id);
          }
        }];
        break bb4;
      }
      let t43;
      if ($[125] !== clearSelectedKnowledgeSource) {
        t43 = () => {
          clearSelectedKnowledgeSource();
        };
        $[125] = clearSelectedKnowledgeSource;
        $[126] = t43;
      } else {
        t43 = $[126];
      }
      const t44 = {
        key: "knowledge-sources",
        label: t("dataAgent.knowledgeSourceChipCount", {
          count: selectedCorpora.length
        }),
        avatar: KNOWLEDGE_SOURCE_CHIP_AVATAR,
        tooltipProps: getKnowledgeSourceChipTooltipProps(selectedCorpora, t),
        onDelete: t43
      };
      let t45;
      if ($[127] !== t44) {
        t45 = [t44];
        $[127] = t44;
        $[128] = t45;
      } else {
        t45 = $[128];
      }
      t41 = t45;
    }
    $[117] = availableCorpora;
    $[118] = clearSelectedKnowledgeSource;
    $[119] = selectedKnowledgeSourceIds;
    $[120] = t;
    $[121] = t41;
  } else {
    t41 = $[121];
  }
  const selectedKnowledgeChips = t41;
  const isLoading = !isKnowledgeOnly && (isLoadingSources || cirrusGroups.isLoading) || isLoadingKnowledgeSources || isLoadingCompanyWideKnowledgeSources;
  let t42;
  let t43;
  if ($[129] !== availableCorpora || $[130] !== cirrusGroups.allOptions || $[131] !== isKnowledgeOnly || $[132] !== isLoading || $[133] !== options || $[134] !== reconcileDatasourceWithAvailableOptions || $[135] !== reconcileKnowledgeSourcesWithAvailableCorpora) {
    t42 = () => {
      if (isLoading) {
        return;
      }
      if (!isKnowledgeOnly) {
        reconcileDatasourceWithAvailableOptions(options, cirrusGroups.allOptions);
      }
      reconcileKnowledgeSourcesWithAvailableCorpora(availableCorpora);
    };
    t43 = [availableCorpora, cirrusGroups.allOptions, isKnowledgeOnly, isLoading, options, reconcileDatasourceWithAvailableOptions, reconcileKnowledgeSourcesWithAvailableCorpora];
    $[129] = availableCorpora;
    $[130] = cirrusGroups.allOptions;
    $[131] = isKnowledgeOnly;
    $[132] = isLoading;
    $[133] = options;
    $[134] = reconcileDatasourceWithAvailableOptions;
    $[135] = reconcileKnowledgeSourcesWithAvailableCorpora;
    $[136] = t42;
    $[137] = t43;
  } else {
    t42 = $[136];
    t43 = $[137];
  }
  useEffect(t42, t43);
  let chips;
  if ($[138] !== selectedCirrusChip || $[139] !== selectedDatasourceChips || $[140] !== selectedKnowledgeChips) {
    chips = [];
    if (selectedCirrusChip != null) {
      chips.push(selectedCirrusChip);
    }
    chips.push(...selectedDatasourceChips, ...selectedKnowledgeChips);
    $[138] = selectedCirrusChip;
    $[139] = selectedDatasourceChips;
    $[140] = selectedKnowledgeChips;
    $[141] = chips;
  } else {
    chips = $[141];
  }
  const selectedChips = chips;
  let t44;
  if ($[142] !== availableCorpora || $[143] !== cirrusGroups.allOptions || $[144] !== datasourceIdSet || $[145] !== isKnowledgeOnly || $[146] !== knowledgeSourceIdSet || $[147] !== options || $[148] !== setSelectedCirrusSource || $[149] !== setSelectedDatasources || $[150] !== setSelectedKnowledgeSources) {
    t44 = (_optionValue, nextValue) => {
      const nextIds = nextValue.map(_temp7);
      if (isKnowledgeOnly) {
        setSelectedKnowledgeSources(nextIds, availableCorpora);
        return;
      }
      const nextKnowledgeIds = nextIds.filter(id_0 => knowledgeSourceIdSet.has(id_0));
      const cirrusOptionId = nextIds.find(isCirrusOptionId);
      if (cirrusOptionId != null) {
        setSelectedCirrusSource(findCirrusSourceOption(cirrusOptionId, cirrusGroups.allOptions));
        setSelectedKnowledgeSources(nextKnowledgeIds, availableCorpora);
        return;
      }
      const nextDatasourceIds_0 = nextIds.filter(id_1 => datasourceIdSet.has(id_1));
      setSelectedDatasources(nextDatasourceIds_0, options);
      setSelectedKnowledgeSources(nextKnowledgeIds, availableCorpora);
    };
    $[142] = availableCorpora;
    $[143] = cirrusGroups.allOptions;
    $[144] = datasourceIdSet;
    $[145] = isKnowledgeOnly;
    $[146] = knowledgeSourceIdSet;
    $[147] = options;
    $[148] = setSelectedCirrusSource;
    $[149] = setSelectedDatasources;
    $[150] = setSelectedKnowledgeSources;
    $[151] = t44;
  } else {
    t44 = $[151];
  }
  const t45 = t(isKnowledgeOnly ? "dataAgent.addExtraContextTitle" : "dataAgent.selectSourcesTitle");
  const t46 = t(isKnowledgeOnly ? "dataAgent.addExtraContextTitle" : "dataAgent.selectSourcesTitle");
  let t47;
  if ($[152] !== isKnowledgeOnly || $[153] !== isLoading || $[154] !== t) {
    t47 = t48 => {
      const {
        onClick
      } = t48;
      return <Button size="small" variant="tonal" leftIcon={<PlusIcon />} onClick={onClick} disabled={isLoading} tooltipProps={{
        title: t(isKnowledgeOnly ? "dataAgent.addExtraContextTooltip" : "dataAgent.addSourceTooltip"),
        kind: "singleline"
      }} />;
    };
    $[152] = isKnowledgeOnly;
    $[153] = isLoading;
    $[154] = t;
    $[155] = t47;
  } else {
    t47 = $[155];
  }
  let t48;
  if ($[156] !== isLoading || $[157] !== menuValue || $[158] !== onMenuClosed || $[159] !== sourceMenuOptions || $[160] !== subCategoryEmptyContent || $[161] !== subOptionsEmptyContent || $[162] !== t44 || $[163] !== t45 || $[164] !== t46 || $[165] !== t47) {
    t48 = <MultiSelectDropdown value={menuValue} options={sourceMenuOptions} subOptionsVariant="submenu" allowSelectAll={false} subOptionsEmptyContent={subOptionsEmptyContent} subCategoryEmptyContent={subCategoryEmptyContent} onChange={t44} onClose={onMenuClosed} buttonTitle={t45} title={t46} dropdownMinWidth="320px" disabled={isLoading} CustomAnchorElement={t47} />;
    $[156] = isLoading;
    $[157] = menuValue;
    $[158] = onMenuClosed;
    $[159] = sourceMenuOptions;
    $[160] = subCategoryEmptyContent;
    $[161] = subOptionsEmptyContent;
    $[162] = t44;
    $[163] = t45;
    $[164] = t46;
    $[165] = t47;
    $[166] = t48;
  } else {
    t48 = $[166];
  }
  let t49;
  if ($[167] !== selectedChips) {
    t49 = selectedChips.map(_temp8);
    $[167] = selectedChips;
    $[168] = t49;
  } else {
    t49 = $[168];
  }
  let t50;
  if ($[169] !== t48 || $[170] !== t49) {
    t50 = <>{t48}{t49}</>;
    $[169] = t48;
    $[170] = t49;
    $[171] = t50;
  } else {
    t50 = $[171];
  }
  return t50;
};
function _temp(corpus) {
  return corpus.availableToInternal;
}
function _temp2(option) {
  return option.id;
}
function _temp3(corpus_0) {
  return corpus_0.id;
}
function _temp4(a, b) {
  return a.name.localeCompare(b.name);
}
function _temp5(datasource_1) {
  return datasource_1.id;
}
function _temp6(datasource_2) {
  return !datasource_2.connected;
}
function _temp7(item_0) {
  return item_0.value;
}
function _temp8(t0) {
  const {
    key,
    label,
    avatar: avatar_0,
    tooltipProps,
    onDelete,
    isError
  } = t0;
  return <Tooltip key={key} {...tooltipProps}><Grid display="inline-flex"><Chip avatar={avatar_0} label={label} onDelete={onDelete} isError={isError} /></Grid></Tooltip>;
}